import { Component } from '@theme/component'
import { requestYieldCallback } from '@theme/maths'
import { useIntersection } from '@theme/use-intersection'

const ProductCardConfig = {
  hoverImageSelector: '.product-card-secondary__hover-media img[data-src]',
  revealMarginPx: 120,
  threshold: 0.01
}

const cardRevealObserver =
  typeof window === 'undefined'
    ? null
    : useIntersection({
        rootMargin: `${ProductCardConfig.revealMarginPx}px 0px`,
        threshold: ProductCardConfig.threshold
      })

const hoverPointerQuery = matchMedia('(hover: hover) and (pointer: fine)')

const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)')

const ignoreInterruptedPlayback = () => undefined

function isWithinRevealWindow(element) {
  if (!(element instanceof HTMLElement)) {
    return true
  }

  const rect = element.getBoundingClientRect()
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth

  return (
    rect.bottom >= -ProductCardConfig.revealMarginPx &&
    rect.top <= viewportHeight + ProductCardConfig.revealMarginPx &&
    rect.right >= 0 &&
    rect.left <= viewportWidth
  )
}

export class ProductCard extends Component {
  requiredRefs = ['productCardLink']

  #mediaHoverAbortController = null
  #isRevealHovered = false
  #revealUnobserve = null

  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)

    if (shouldOpenInNewTab) {
      event.preventDefault()
      window.open(url.href, '_blank')
      return
    }
    window.location.href = url.href
  }

  #handleRevealEnter = (event) => {
    if (!this.#isMousePointer(event)) {
      return
    }

    this.#isRevealHovered = true

    if (reducedMotionQuery.matches) {
      this.#playRevealVideo()
    }
  }

  #handleRevealTransitionEnd = (event) => {
    if (event.propertyName !== 'clip-path') {
      return
    }

    if (!this.#isRevealHovered) {
      return
    }

    this.#playRevealVideo()
  }

  #handleRevealLeave = () => {
    this.#isRevealHovered = false
    this.#pauseRevealVideo()
  }

  #hydrateHoverImage = () => {
    const image = this.querySelector(ProductCardConfig.hoverImageSelector)
    if (!(image instanceof HTMLImageElement)) {
      return
    }

    image.loading = 'eager'
    if (image.dataset.sizes) image.sizes = image.dataset.sizes
    if (image.dataset.srcset) image.srcset = image.dataset.srcset
    if (image.dataset.src) image.src = image.dataset.src
    delete image.dataset.sizes
    delete image.dataset.srcset
    delete image.dataset.src
  }

  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) {
      return
    }

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"]'
    )

    if (interactiveElement) {
      return
    }

    const link = this.refs.productCardLink
    if (!link.href) {
      return
    }
    const linkURL = new URL(link.href)

    const productCardAnchor = link.getAttribute('id')
    if (!productCardAnchor) {
      return
    }

    const url = new URL(window.location.href)
    const parent = this.closest('li')
    url.hash = productCardAnchor
    if (parent?.dataset.page) {
      url.searchParams.set('page', parent.dataset.page)
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString())
      })
    }

    const targetLink = event.target.closest('a')

    if (!targetLink) {
      this.#navigateToURL(event, linkURL)
    }
  }

  connectedCallback() {
    super.connectedCallback()

    const link = this.refs.productCardLink
    if (!(link instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found')
    }
    this.#setupMediaHover()
    this.#setupScrollReveal()

    this.addEventListener('click', this.navigateToProduct)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.#teardownMediaHover()
    this.#teardownScrollReveal()
    this.removeEventListener('click', this.navigateToProduct)
  }

  #setupScrollReveal() {
    this.#teardownScrollReveal()

    if (this.dataset.cardLazy !== 'true') {
      this.dataset.cardReveal = 'visible'
      return
    }

    if (reducedMotionQuery.matches || isWithinRevealWindow(this)) {
      this.dataset.cardReveal = 'visible'
      return
    }

    this.dataset.cardReveal = 'pending'

    if (!cardRevealObserver) {
      this.dataset.cardReveal = 'visible'
      return
    }

    this.#revealUnobserve = cardRevealObserver.observe(this, (entry) => {
      if (!entry.isIntersecting) {
        return
      }

      this.dataset.cardReveal = 'visible'
      this.#teardownScrollReveal()
    })
  }

  #teardownScrollReveal() {
    if (typeof this.#revealUnobserve === 'function') {
      this.#revealUnobserve()
    }
    this.#revealUnobserve = null
  }

  #setupMediaHover() {
    this.#teardownMediaHover()

    if (!hoverPointerQuery.matches) {
      return
    }

    this.#mediaHoverAbortController = new AbortController()
    const { signal } = this.#mediaHoverAbortController
    this.#bindHoverImage(signal)
    this.#bindRevealVideo(signal)
  }

  #bindHoverImage(signal) {
    if (!this.querySelector(ProductCardConfig.hoverImageSelector)) {
      return
    }

    this.addEventListener('pointerenter', this.#hydrateHoverImage, {
      once: true,
      signal
    })
    this.addEventListener('focusin', this.#hydrateHoverImage, {
      once: true,
      signal
    })
  }

  #bindRevealVideo(signal) {
    const gallery = this.refs.productCardLink
    if (!gallery) {
      return
    }

    const revealLayer = this.querySelector('.product-card-img--reveal')
    if (!revealLayer) {
      return
    }

    if (!this.#getRevealVideo()) {
      return
    }

    gallery.addEventListener('pointerenter', this.#handleRevealEnter, {
      signal
    })
    gallery.addEventListener('pointerleave', this.#handleRevealLeave, {
      signal
    })
    revealLayer.addEventListener(
      'transitionend',
      this.#handleRevealTransitionEnd,
      { signal }
    )
  }

  #teardownMediaHover() {
    this.#mediaHoverAbortController?.abort()
    this.#mediaHoverAbortController = null
    this.#isRevealHovered = false
  }

  #getRevealVideo() {
    return this.querySelector(
      '.product-card-img--reveal ui-video[data-product-card-video]'
    )
  }

  #playRevealVideo() {
    const revealVideo = this.#getRevealVideo()
    if (!revealVideo) {
      return
    }

    revealVideo.paused = false

    const video = revealVideo.querySelector('video')
    if (!video) {
      revealVideo.play()
      return
    }

    if (video.currentTime !== 0) {
      video.currentTime = 0
    }
    video.play()?.catch(ignoreInterruptedPlayback)
  }

  #pauseRevealVideo() {
    const revealVideo = this.#getRevealVideo()
    if (!revealVideo) {
      return
    }

    revealVideo.paused = true

    const video = revealVideo.querySelector('video')
    if (!video) {
      if (revealVideo.hasAttribute('loaded')) {
        revealVideo.player.pause()
      }
      return
    }

    video.pause()
  }

  #isMousePointer(event) {
    return !event.pointerType || event.pointerType === 'mouse'
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard)
}
